import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  QrCode, Package, 
  MapPin, ArrowLeft, AlertCircle 
} from 'lucide-react';
import inventoryService from '../services/inventoryService';

const LabelBipScannerPage = () => {
  const { code } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [itemData, setItemData] = useState(null);
  const [itemType, setItemType] = useState('');

  // Identificador da etiqueta a ser buscada (parâmetro code da rota)
  const lookupCode = code;

  useEffect(() => {
    if (!lookupCode) {
      setError('Nenhum código de etiqueta fornecido.');
      setLoading(false);
      return;
    }

    const fetchLabelData = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await inventoryService.findStockByQRCode(lookupCode);
        if (response.success && response.data) {
          setItemData(response.data);
          setItemType(response.type || 'STOCK_LABEL');
        } else {
          setError(response.message || 'Etiqueta não encontrada no sistema.');
        }
      } catch (err) {
        console.error('Erro ao ler etiqueta:', err);
        setError(err.response?.data?.message || 'Não foi possível carregar as informações desta etiqueta.');
      } finally {
        setLoading(false);
      }
    };

    fetchLabelData();
  }, [lookupCode]);

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 p-4 sm:p-6 flex flex-col items-center justify-center">
      <div className="w-full max-w-lg bg-slate-800 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
        
        {/* Header Superior */}
        <div className="bg-gradient-to-r from-amber-600 to-amber-700 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center backdrop-blur-sm">
              <QrCode className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white tracking-wide">Leitor de Peça / Estoque</h1>
              <p className="text-xs text-amber-200">Marcenaria Santiago • Consulta Rápida</p>
            </div>
          </div>
          <button
            onClick={() => navigate('/estoque')}
            className="p-2 rounded-lg bg-black/20 hover:bg-black/40 text-white transition-colors"
            title="Voltar ao Estoque"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        </div>

        {/* Conteúdo */}
        <div className="p-6">
          {loading ? (
            <div className="py-12 flex flex-col items-center justify-center text-center">
              <div className="w-10 h-10 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-sm text-slate-300">Consultando código da peça no sistema...</p>
              <span className="text-xs font-mono text-slate-500 mt-1">{lookupCode}</span>
            </div>
          ) : error ? (
            <div className="py-8 text-center space-y-4">
              <div className="w-14 h-14 mx-auto rounded-full bg-rose-500/20 text-rose-400 flex items-center justify-center">
                <AlertCircle className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-base font-semibold text-rose-300">Etiqueta Não Encontrada</h3>
                <p className="text-xs text-slate-400 mt-1">{error}</p>
                <p className="text-[11px] font-mono text-slate-500 mt-2 bg-slate-900/60 p-2 rounded border border-slate-800 inline-block">
                  Código: {lookupCode}
                </p>
              </div>
              <div>
                <button
                  onClick={() => navigate('/estoque')}
                  className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-xs font-medium rounded-lg transition-colors"
                >
                  Ir para Estoque Geral
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Badge de Tipo */}
              <div className="flex items-center justify-between border-b border-slate-700/80 pb-3">
                <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  {itemType === 'RETALHO' ? 'Sobra / Retalho MDF' : 
                   itemType === 'CUTTING_PART' ? 'Peça de Produção (Corte)' : 'Item de Estoque Físico'}
                </span>
                <span className="text-xs font-mono text-slate-400">
                  {itemData.qr_code || itemData.codigo_etiqueta || itemData.label_code || lookupCode}
                </span>
              </div>

              {/* Informações Principais */}
              <div>
                <h2 className="text-xl font-bold text-white mb-1">
                  {itemData.produto_nome || itemData.part_name || 'Peça sem nome'}
                </h2>
                {itemData.produto_sku && (
                  <p className="text-xs font-mono text-slate-400">SKU: {itemData.produto_sku}</p>
                )}
              </div>

              {/* Grid de Detalhes Técnicos */}
              <div className="grid grid-cols-2 gap-3 bg-slate-900/60 p-4 rounded-xl border border-slate-700/50">
                {/* Medidas / Dimensões */}
                <div>
                  <span className="text-[11px] uppercase font-bold text-slate-400 block mb-0.5">Medidas / Formato</span>
                  <p className="text-sm font-semibold text-amber-300">
                    {itemData.comprimento_cm && itemData.largura_cm ? (
                      `${itemData.comprimento_cm} cm × ${itemData.largura_cm} cm`
                    ) : itemData.width && itemData.height ? (
                      `${itemData.width} mm × ${itemData.height} mm`
                    ) : (
                      itemData.tamanho_label || itemData.medida_text || 'Padrão'
                    )}
                  </p>
                </div>

                {/* Espessura / Cor */}
                <div>
                  <span className="text-[11px] uppercase font-bold text-slate-400 block mb-0.5">Acabamento / Espessura</span>
                  <p className="text-sm text-slate-200">
                    {itemData.cor || 'Padrão'}
                    {(itemData.espessura_mm || itemData.thickness) && (
                      <span className="text-slate-400 ml-1">({itemData.espessura_mm || itemData.thickness} mm)</span>
                    )}
                  </p>
                </div>

                {/* Localização */}
                <div className="col-span-2 border-t border-slate-800 pt-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-amber-400 shrink-0" />
                  <span className="text-xs text-slate-300">
                    <strong>Localização:</strong> {itemData.localizacao || itemData.armazem_localizacao || itemData.armazem_nome || 'Oficina Geral'}
                  </span>
                </div>

                {/* Se for peça de produção: Projeto / Cliente */}
                {(itemData.project_name || itemData.client_name) && (
                  <div className="col-span-2 border-t border-slate-800 pt-2 text-xs text-slate-300">
                    <p><strong>Projeto:</strong> {itemData.project_name || 'N/A'}</p>
                    <p><strong>Cliente:</strong> {itemData.client_name || 'N/A'}</p>
                    {itemData.board_number && <p><strong>Chapa do Corte:</strong> #{itemData.board_number}</p>}
                  </div>
                )}
              </div>

              {/* Ações Rápidas no Chão de Fábrica */}
              <div className="space-y-2 pt-2">
                <button
                  onClick={() => navigate('/estoque')}
                  className="w-full py-3 bg-amber-600 hover:bg-amber-500 text-white font-medium text-sm rounded-xl transition-colors shadow-lg shadow-amber-600/30 flex items-center justify-center gap-2"
                >
                  <Package className="w-4 h-4" />
                  Abrir no Painel de Estoque
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Rodapé */}
        <div className="bg-slate-950/60 px-6 py-3 border-t border-slate-700/60 text-center">
          <p className="text-[11px] text-slate-500">
            Sistema Santiago • Rastreabilidade Física com Etiquetas QR / Code 128
          </p>
        </div>

      </div>
    </div>
  );
};

export default LabelBipScannerPage;
